// AstriaMexicoCard.tsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Share2, MessageCircleHeart, Copy, Check } from "lucide-react";
import { MX_HEADING_FONT } from "./astriaMexicoTheme";
import { useMexicoColors } from "./AstriaMexicoThemeContext";

// ─── Astria Mexico — result-card primitives ────────────────────────────────
// Clean-outline cards, soft-glow hover, warm terracotta accent. Every color
// is read from useMexicoColors() so cards repaint instantly when the shell's
// light/dark toggle flips.

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

interface AstriaMexicoCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  delay?: number;
  shareText?: string;
}

export const AstriaMexicoResultCard: React.FC<AstriaMexicoCardProps> = ({
  icon,
  title,
  subtitle,
  children,
  delay = 0,
  shareText,
}) => {
  const { colors } = useMexicoColors();
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      custom={delay}
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        background: colors.card,
        border: `1px solid ${hovered ? colors.accent : colors.cardBorder}`,
        borderRadius: 14,
        padding: 20,
        boxShadow: hovered ? `0 8px 28px ${colors.highlightSoft}` : "0 2px 10px rgba(0,0,0,0.04)",
        transition:
          "box-shadow 0.3s ease, border-color 0.3s ease, background-color 0.3s ease, color 0.3s ease",
      }}
    >
      <div className="flex items-center gap-3" style={{ marginBottom: 18 }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            border: `1.5px solid ${colors.accent}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.accent,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 19,
              fontWeight: 700,
              color: colors.titleColor,
              fontFamily: MX_HEADING_FONT,
              lineHeight: 1.25,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 12.5, color: colors.mutedColor, marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>

      <div>{children}</div>

      <AstriaMexicoShareRow text={shareText} />
    </motion.div>
  );
};

interface FieldBlockProps {
  label: string;
  value: string;
}

export const AstriaMexicoFieldBlock: React.FC<FieldBlockProps> = ({ label, value }) => {
  const { colors } = useMexicoColors();
  if (!value || !value.trim()) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: colors.accent,
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <p
        style={{
          fontSize: 15,
          lineHeight: 1.75,
          color: colors.bodyColor,
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </p>
    </div>
  );
};

interface ActionListProps {
  label: string;
  items: string[];
}

export const AstriaMexicoActionList: React.FC<ActionListProps> = ({ label, items }) => {
  const { colors } = useMexicoColors();
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: colors.accent,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <ul style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item, i) => (
          <li
            key={i}
            className="flex items-start gap-2"
            style={{ fontSize: 14.5, lineHeight: 1.6, color: colors.bodyColor }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: colors.highlight,
                marginTop: 8,
                flexShrink: 0,
              }}
            />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AstriaMexicoShareRow: React.FC<{ text?: string }> = ({ text }) => {
  const { colors } = useMexicoColors();
  const [copied, setCopied] = useState(false);
  if (!text) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — silently ignore
    }
  };

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginTop: 20,
        paddingTop: 18,
        borderTop: `1px solid ${colors.cardBorder}`,
        flexWrap: "wrap",
      }}
    >
      <button
        onClick={handleWhatsApp}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{ paddingInline: 14, paddingBlock: 9, background: "#25D366", color: "#fff" }}
      >
        <Share2 className="w-3.5 h-3.5" />
        WhatsApp
      </button>
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold border transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderColor: colors.cardBorder,
          color: colors.bodyColor,
          background: colors.card,
        }}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5" style={{ color: colors.accent }} />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
        {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
};

export const MXLoadingState: React.FC<{ label?: string }> = ({
  label = "Leyendo con calma, un momento…",
}) => {
  const { colors } = useMexicoColors();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "70px 20px",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          border: `3px solid ${colors.section}`,
          borderTopColor: colors.accent,
          borderRadius: "50%",
        }}
        className="animate-spin"
      />
      <p style={{ fontSize: 13.5, color: colors.mutedColor }}>{label}</p>
    </div>
  );
};

export const MXErrorState: React.FC<{
  message: string;
  onRetry: () => void;
}> = ({ message, onRetry }) => {
  const { colors } = useMexicoColors();
  return (
    <div
      style={{
        padding: "18px 20px",
        background: "#FBEAE4",
        border: "1px solid #E8C3B3",
        borderRadius: 14,
        color: "#7A3D28",
        fontSize: 14,
      }}
    >
      {message}
      <button
        onClick={onRetry}
        style={{
          display: "block",
          marginTop: 10,
          color: colors.accent,
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        Intentar de Nuevo
      </button>
    </div>
  );
};

export const MXChatCta: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { colors } = useMexicoColors();
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-center gap-2 rounded-full text-sm font-semibold transition-all hover:scale-[1.02]"
      style={{
        marginTop: 24,
        width: "100%",
        color: colors.white,
        background: colors.accent,
        paddingTop: 14,
        paddingBottom: 14,
      }}
    >
      <MessageCircleHeart className="w-4 h-4" />
      Continuar en el Chat
    </button>
  );
};
